import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { 
  Wine, 
  ChevronRight, 
  Trophy, 
  Clock, 
  AlertCircle, 
  Beaker,
  Users,
  Timer,
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GroupCompeteTestPage() {
  const { accessCode } = useParams();
  const [competitionData, setCompetitionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Test state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Ingredients-only test state
  const [ingredientAnswers, setIngredientAnswers] = useState({});

  useEffect(() => {
    fetchCompetition();
  }, [accessCode]);

  const fetchCompetition = async () => {
    try {
      const response = await axios.get(`${API}/group-competitions/by-code/${accessCode}`);
      setCompetitionData(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError("Competition not found");
      } else if (error.response?.status === 400) {
        setError(error.response?.data?.detail || "Competition is no longer available");
      } else {
        setError("Failed to load competition");
      }
    } finally {
      setLoading(false);
    }
  };

  const competition = competitionData?.competition;
  const test = competitionData?.test;
  const cocktails = competitionData?.cocktails || [];
  const ingredientsQuestions = competitionData?.ingredients_questions || [];
  const player = competitionData?.player;
  const completedCount = competitionData?.completed_count || 0;
  const totalPlayers = competitionData?.total_players || 0;
  const currentCocktail = cocktails[currentQuestion];
  const testType = test?.test_type || "full";
  const isIngredientsOnly = testType === "ingredients";
  
  const currentIngredientQuestion = isIngredientsOnly ? ingredientsQuestions[currentQuestion] : null;
  const totalQuestions = isIngredientsOnly ? ingredientsQuestions.length : cocktails.length;

  const generateOptions = (cocktail, field, allCocktails) => {
    if (!cocktail) return [];
    const correctValue = cocktail[field] || "None";
    let options = new Set([correctValue]);
    
    allCocktails.forEach(c => {
      if (c.id !== cocktail.id && c[field]) {
        options.add(c[field]);
      }
    });
    
    const genericOptions = {
      garnish: ["Lime wheel", "Orange peel", "Cherry", "Mint sprig", "None", "Olive", "Lemon twist"],
      glassware: ["Rocks", "Coupe", "Highball", "Martini", "Collins", "Nick & Nora", "Flute"],
      side_elements: ["None", "N/A", "Espresso shot", "Bitters on the side", "Sugar rim", "Salt rim"]
    };
    
    if (genericOptions[field]) {
      genericOptions[field].forEach(opt => options.add(opt));
    }
    
    return Array.from(options).slice(0, 4).sort(() => Math.random() - 0.5);
  };

  const generateIngredientOptions = (cocktail, allCocktails) => {
    if (!cocktail) return [];
    const correctIngredients = cocktail.ingredients?.map(i => 
      `${i.measurement} ${i.name}${i.brand ? ` (${i.brand})` : ''}`
    ).join(", ") || "None";
    
    let options = new Set([correctIngredients]);
    
    allCocktails.forEach(c => {
      if (c.id !== cocktail.id && c.ingredients?.length > 0) {
        const ing = c.ingredients.map(i => 
          `${i.measurement} ${i.name}${i.brand ? ` (${i.brand})` : ''}`
        ).join(", ");
        options.add(ing);
      }
    });
    
    return Array.from(options).slice(0, 4).sort(() => Math.random() - 0.5);
  };

  const getQuestionsForTestType = () => {
    switch (testType) {
      case "ingredients":
        return ["ingredients"];
      case "presentation":
        return ["garnish", "glassware"];
      case "full":
      default:
        return ["ingredients", "garnish", "glassware", "side_elements"];
    }
  };

  const questions = getQuestionsForTestType();

  const startTest = () => {
    setTestStarted(true);
  };

  const handleAnswer = (field, value) => {
    if (!currentCocktail) return;
    setAnswers(prev => ({
      ...prev,
      [currentCocktail.id]: {
        ...prev[currentCocktail.id],
        [field]: value
      }
    }));
  };

  const handleIngredientAnswer = (questionIndex, value) => {
    setIngredientAnswers(prev => ({
      ...prev,
      [questionIndex]: value
    }));
  };

  const nextQuestion = () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const calculateResults = () => {
    if (isIngredientsOnly) {
      let correctCount = 0;
      const cocktailResults = {};
      
      ingredientsQuestions.forEach((q, idx) => {
        const userAnswer = ingredientAnswers[idx];
        const isCorrect = userAnswer === q.correct_answer;
        if (isCorrect) correctCount++;
        
        if (!cocktailResults[q.cocktail_id]) {
          cocktailResults[q.cocktail_id] = {
            cocktail_name: q.cocktail_name,
            correct: 0,
            total: 0
          };
        }
        cocktailResults[q.cocktail_id].total++;
        if (isCorrect) cocktailResults[q.cocktail_id].correct++;
      });
      
      const totalPossible = ingredientsQuestions.length;
      const percentage = totalPossible > 0 ? (correctCount / totalPossible) * 100 : 0;
      
      return { 
        answers: Object.values(cocktailResults), 
        totalScore: correctCount, 
        totalPossible, 
        percentage,
        isIngredientsTest: true
      };
    }
    
    const questionResults = cocktails.map(cocktail => {
      const userAnswers = answers[cocktail.id] || {};
      const correctIngredients = cocktail.ingredients?.map(i => 
        `${i.measurement} ${i.name}${i.brand ? ` (${i.brand})` : ''}`
      ).join(", ") || "None";
      
      const ingredientsCorrect = questions.includes("ingredients") 
        ? userAnswers.ingredients === correctIngredients 
        : true;
      const garnishCorrect = questions.includes("garnish") 
        ? userAnswers.garnish === (cocktail.garnish || "None")
        : true;
      const glasswareCorrect = questions.includes("glassware")
        ? userAnswers.glassware === cocktail.glassware
        : true;
      const sideCorrect = questions.includes("side_elements")
        ? userAnswers.side_elements === (cocktail.side_elements || "None" || "N/A")
        : true;
      
      const score = [
        questions.includes("ingredients") && ingredientsCorrect,
        questions.includes("garnish") && garnishCorrect,
        questions.includes("glassware") && glasswareCorrect,
        questions.includes("side_elements") && sideCorrect
      ].filter(Boolean).length;
      
      return {
        cocktail_name: cocktail.name,
        score
      };
    });

    const totalScore = questionResults.reduce((sum, r) => sum + r.score, 0);
    const totalPossible = cocktails.length * questions.length;
    const percentage = totalPossible > 0 ? (totalScore / totalPossible) * 100 : 0;

    return { answers: questionResults, totalScore, totalPossible, percentage };
  };

  const submitTest = async () => {
    setSubmitting(true);
    const calculatedResults = calculateResults();
    
    try {
      // First save the test result
      const resultResponse = await axios.post(`${API}/test-results`, {
        staff_id: player.staff_id,
        test_id: test.id,
        test_name: test.name,
        score: calculatedResults.totalScore,
        total_possible: calculatedResults.totalPossible,
        percentage: calculatedResults.percentage,
        answers: calculatedResults.answers
      });
      
      // Then update the competition
      await axios.put(
        `${API}/group-competitions/${competition.id}/submit`,
        null,
        {
          params: {
            access_code: accessCode,
            result_id: resultResponse.data.id,
            score: calculatedResults.totalScore,
            percentage: calculatedResults.percentage
          }
        }
      );
      
      setResults(calculatedResults);
      setTestCompleted(true);
      toast.success("Test submitted!");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit test");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-[hsl(var(--muted-foreground))]">Loading competition...</div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Unable to Load Competition</h2>
            <p className="text-[hsl(var(--muted-foreground))]">{error}</p>
          </CardContent>
        </Card>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // Waiting for results screen
  if (testCompleted && results) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-24">
        <div className="max-w-2xl mx-auto space-y-6">
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-8 text-center">
              <Trophy size={64} className={`mx-auto mb-4 ${
                results.percentage >= 80 ? 'text-green-500' : results.percentage >= 60 ? 'text-[#C36A2F]' : 'text-red-500'
              }`} />
              <h1 className="text-3xl font-bold mb-2" data-testid="test-complete-title">Submitted!</h1>
              <p className="text-[hsl(var(--muted-foreground))] mb-2">{player?.staff_name}</p>
              <span className="inline-block px-3 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400 mb-6">
                <Users size={12} className="inline mr-1" /> Group Competition
              </span>
              
              <div className={`inline-block px-8 py-4 rounded-xl border-2 ${
                results.percentage >= 80 
                  ? 'border-green-500 bg-green-500/10' 
                  : results.percentage >= 60 
                  ? 'border-[#C36A2F] bg-[#C36A2F]/10'
                  : 'border-red-500 bg-red-500/10'
              }`}>
                <p className={`text-5xl font-bold ${
                  results.percentage >= 80 ? 'text-green-500' : results.percentage >= 60 ? 'text-[#C36A2F]' : 'text-red-500'
                }`} data-testid="final-score">
                  {results.percentage.toFixed(0)}%
                </p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {results.totalScore} / {results.totalPossible} points
                </p>
              </div>
              
              <div className="mt-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <Timer className="text-blue-400 mx-auto mb-2" size={24} />
                <p className="text-blue-400 font-medium">Waiting for other players...</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Final rankings will be available once everyone completes the test or the deadline passes.
                </p>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-[hsl(var(--muted-foreground))]">
            Your results have been saved. You can close this page.
          </p>

          {test?.id && (
            <div className="text-center">
              <Link to={`/study/${test.id}`}>
                <Button
                  variant="outline"
                  className="border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
                  data-testid="study-flashcards-btn"
                >
                  <BookOpen size={18} className="mr-2" />
                  Study These Cocktails
                </Button>
              </Link>
            </div>
          )}
        </div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // Start Screen
  if (!testStarted) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <Users size={48} className="mx-auto text-purple-400 mb-4" />
              <span className="inline-block px-3 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400 mb-4">
                Group Competition
              </span>
              <h1 className="text-2xl font-bold mb-2">{competition?.name}</h1>
              <p className="text-[hsl(var(--muted-foreground))]">{test?.name}</p>
              
              <div className="mt-4 p-4 rounded-lg bg-[hsl(var(--secondary))]">
                <p className="font-medium text-lg text-[hsl(var(--primary))]">
                  Welcome, {player?.staff_name}!
                </p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                  {completedCount}/{totalPlayers} players have completed
                </p>
              </div>
              
              <div className="mt-4 space-y-2 text-sm text-[hsl(var(--muted-foreground))]">
                {isIngredientsOnly ? (
                  <>
                    <p>{ingredientsQuestions.length} ingredient questions</p>
                    <p className="text-[hsl(var(--primary))]">
                      Format: &quot;How much of [ingredient] is in [cocktail]?&quot;
                    </p>
                  </>
                ) : (
                  <>
                    <p>{cocktails.length} cocktails</p>
                    <p>{questions.length} question{questions.length > 1 ? 's' : ''} per cocktail</p>
                  </>
                )}
              </div>
              
              <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <p className="text-xs text-blue-400">
                  <Timer size={12} className="inline mr-1" />
                  Results shown when all players finish
                </p>
              </div>
            </div>

            <Button
              onClick={startTest}
              data-testid="start-test-btn"
              className="w-full btn-glow bg-purple-500 text-white hover:bg-purple-600"
            >
              Start Competition
              <ChevronRight size={18} className="ml-2" />
            </Button>
          </CardContent>
        </Card>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // Quiz Screen
  const isIngredientQuestionAnswered = isIngredientsOnly 
    ? ingredientAnswers[currentQuestion] !== undefined 
    : false;
  const currentAnswers = !isIngredientsOnly && currentCocktail ? (answers[currentCocktail.id] || {}) : {};
  const answeredQuestions = isIngredientsOnly 
    ? (isIngredientQuestionAnswered ? 1 : 0)
    : questions.filter(q => currentAnswers[q]).length;
  const isQuestionComplete = isIngredientsOnly 
    ? isIngredientQuestionAnswered 
    : answeredQuestions === questions.length;
  const isLastQuestion = currentQuestion === totalQuestions - 1;

  // Ingredients-only quiz
  if (isIngredientsOnly && currentIngredientQuestion) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-24">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400">
                Competition
              </span>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Question {currentQuestion + 1} of {totalQuestions}
              </p>
            </div>
          </div>

          <Progress value={(currentQuestion / totalQuestions) * 100} className="h-2" />

          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <span className="inline-block px-3 py-1 rounded-full text-xs bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] mb-4">
                  {currentIngredientQuestion.cocktail_name}
                </span>
                <h2 className="text-xl md:text-2xl font-bold text-[hsl(var(--foreground))]" data-testid="ingredient-question">
                  How much of <span className="text-[hsl(var(--primary))]">{currentIngredientQuestion.ingredient_name}</span> is in the <span className="text-[hsl(var(--primary))]">{currentIngredientQuestion.cocktail_name}</span>?
                </h2>
              </div>
              
              <RadioGroup 
                value={ingredientAnswers[currentQuestion] || ""} 
                onValueChange={(val) => handleIngredientAnswer(currentQuestion, val)}
                className="space-y-3"
              >
                {currentIngredientQuestion.options.map((opt, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-center space-x-3 p-4 rounded-lg cursor-pointer transition-all ${
                      ingredientAnswers[currentQuestion] === opt 
                        ? 'border-purple-500 bg-purple-500/10' 
                        : 'border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]'
                    } border`}
                    data-testid={`option-${idx}`}
                  >
                    <RadioGroupItem value={opt} id={`ing-opt-${idx}`} />
                    <Label htmlFor={`ing-opt-${idx}`} className="text-base cursor-pointer flex-1 font-medium">{opt}</Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            {currentQuestion > 0 && (
              <Button variant="outline" onClick={prevQuestion} data-testid="prev-btn">
                Previous
              </Button>
            )}
            <div className="flex-1" />
            {isLastQuestion ? (
              <Button
                onClick={submitTest}
                disabled={!isQuestionComplete || submitting}
                data-testid="submit-test-btn"
                className="btn-glow bg-purple-500 text-white hover:bg-purple-600"
              >
                {submitting ? "Submitting..." : "Submit"}
              </Button>
            ) : (
              <Button
                onClick={nextQuestion}
                disabled={!isQuestionComplete}
                data-testid="next-btn"
                className="bg-purple-500 text-white hover:bg-purple-600"
              >
                Next
                <ChevronRight size={18} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // Regular quiz for other test types
  const ingredientOptions = currentCocktail ? generateIngredientOptions(currentCocktail, cocktails) : [];
  const garnishOptions = currentCocktail ? generateOptions(currentCocktail, 'garnish', cocktails) : [];
  const glasswareOptions = currentCocktail ? generateOptions(currentCocktail, 'glassware', cocktails) : [];
  const sideOptions = currentCocktail ? generateOptions(currentCocktail, 'side_elements', cocktails) : [];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-24">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400">
              Competition
            </span>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Question {currentQuestion + 1} of {totalQuestions}
            </p>
          </div>
        </div>

        <Progress value={(currentQuestion / totalQuestions) * 100} className="h-2" />

        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="p-6 text-center">
            <h2 className="text-3xl font-bold text-[hsl(var(--primary))]">{currentCocktail?.name}</h2>
            <p className="text-[hsl(var(--muted-foreground))]">{currentCocktail?.category} • {currentCocktail?.base_spirit}</p>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {questions.includes("ingredients") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">What are the ingredients?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.ingredients || ""} 
                  onValueChange={(val) => handleAnswer('ingredients', val)}
                >
                  {ingredientOptions.map((opt, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-start space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.ingredients === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`ing-${idx}`} />
                      <Label htmlFor={`ing-${idx}`} className="text-sm cursor-pointer flex-1">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {questions.includes("garnish") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">What is the garnish?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.garnish || ""} 
                  onValueChange={(val) => handleAnswer('garnish', val)}
                >
                  {garnishOptions.map((opt, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.garnish === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`gar-${idx}`} />
                      <Label htmlFor={`gar-${idx}`} className="cursor-pointer">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {questions.includes("glassware") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">What glassware is used?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.glassware || ""} 
                  onValueChange={(val) => handleAnswer('glassware', val)}
                >
                  {glasswareOptions.map((opt, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.glassware === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`glass-${idx}`} />
                      <Label htmlFor={`glass-${idx}`} className="cursor-pointer">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {questions.includes("side_elements") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Any side elements?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.side_elements || ""} 
                  onValueChange={(val) => handleAnswer('side_elements', val)}
                >
                  {sideOptions.map((opt, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.side_elements === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`side-${idx}`} />
                      <Label htmlFor={`side-${idx}`} className="cursor-pointer">{opt || "None"}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex gap-3">
          {currentQuestion > 0 && (
            <Button variant="outline" onClick={prevQuestion}>
              Previous
            </Button>
          )}
          <div className="flex-1" />
          {isLastQuestion ? (
            <Button
              onClick={submitTest}
              disabled={!isQuestionComplete || submitting}
              className="btn-glow bg-purple-500 text-white hover:bg-purple-600"
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          ) : (
            <Button
              onClick={nextQuestion}
              disabled={!isQuestionComplete}
              className="bg-purple-500 text-white hover:bg-purple-600"
            >
              Next
              <ChevronRight size={18} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
